import { signal } from '@preact/signals';
import type { AppServices } from '../app';
import type { Place } from '../astro/frame';
import { starName } from '../data/catalog';
import { enuToAltAz, refractEnu, wrap180, type Vec3 } from '../lib/math';
import { alignReticle, loadJSON, place, previewFrom, reliving, saveJSON, selection, showToast, simNow, timeLive } from '../state';
import { crescentDay } from './saudi';
import { distanceKm } from './skycheck';

// Align with the sky (the owner, 24 October 2026, DECISIONS 111): one tap lines the sky on the screen up with the real
// one when the phone's compass is out. Ahillah picks something easy to find (the Moon, else a bright planet, else one of
// the brightest stars, comfortably up), says where to look, and the person holds the phone up so the circle in the middle
// covers the real one. Never the Sun; never the Moon on the 29th, 30th or 1st (the owner's rule: nothing is said then of
// where it is), nor a Moon close to the Sun; nothing nearly overhead, where a small slip of the hand is a big turn of the
// compass. Only with the sky here and now on show: lining up with another time or place would turn the real sky wrong.
// The correction is kept for the night and the place, not for ever: a compass's error changes with where the phone is
// and what is near it.

export interface AlignTarget {
  id: string;
  /** As it reads in a sentence: "the Moon", "Jupiter", "Vega". */
  label: string;
  enu: Vec3;
  alt: number;
  az: number;
}

/** Below this the horizon, trees and houses get in the way, and the air bends the light most (degrees). */
const LOWEST = 10;
/** Above this, nearly overhead, a small slip of the hand is a big turn of the compass (degrees). */
const HIGHEST = 70;
/** The Moon this close to the Sun is thin, hard to find, and sends the eye towards the Sun (degrees). */
const MOON_FROM_SUN = 40;

/** The sky on show is the real one in front of the person: now, here, and not a saved night. */
export const hereAndNow = () => timeLive.peek() && !previewFrom.peek() && !reliving.peek();
/** Kept this long and this near: about a night, about a town. */
export const KEEP_HOURS = 12;
export const KEEP_KM = 30;
/** A turn bigger than this is more than a compass is usually out: it is checked first (degrees). */
export const BIG_TURN = 40;
/** The phone pointing higher than this gives no bearing worth having (degrees). */
const READING_HIGHEST = 80;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** What to line up with, best first: the Moon, then bright planets, then the brightest stars, each easiest a little up. */
export function alignTargets(s: AppServices, t = simNow().getTime(), pl: Place = place.peek()): AlignTarget[] {
  const eng = s.engine;
  // By day only the Moon shows.
  const day = eng.context.sunAlt > -4;
  const out: (AlignTarget & { rank: number })[] = [];
  const add = (id: string, label: string, enu: Vec3, rank: number) => {
    const w = enuToAltAz(refractEnu(enu));
    if (w.alt < LOWEST || w.alt > HIGHEST) return;
    // The Moon before planets before stars, whatever their heights; within each, nearest 40° up first.
    out.push({ id, label, enu, alt: w.alt, az: w.az, rank: rank * 1000 + Math.abs(w.alt - 40) });
  };
  const sun = eng.bodies.find((b) => b.id === 'Sun');
  const fromSun = (v: Vec3) => (sun ? Math.acos(Math.max(-1, Math.min(1, v[0] * sun.dir[0] + v[1] * sun.dir[1] + v[2] * sun.dir[2]))) * (180 / Math.PI) : 180);
  for (const b of eng.bodies) {
    if (b.id === 'Sun') continue;
    if (b.id === 'Moon') {
      if (!crescentDay(t, pl) && fromSun(b.dir) >= MOON_FROM_SUN) add('Moon', 'the Moon', eng.frame.toEnu(b.dir), 0);
    } else if (!day && b.mag <= 1) add(b.id, b.id, eng.frame.toEnu(b.dir), 1);
  }
  if (!day) {
    const cat = eng.stars;
    for (const i of cat.named) {
      if (cat.mag[i] > 1) break;
      add(`star-${i}`, starName(cat, i), eng.frame.toEnu(eng.starDir(i)), 2);
    }
  }
  return out.sort((a, b) => a.rank - b.rank).map(({ rank: _, ...x }) => x);
}

interface Kept { value?: unknown; at?: unknown; lat?: unknown; lon?: unknown }
const num = (v: unknown, lo: number, hi: number): v is number => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;

/** The correction kept from Align, if it was made in the last 12 hours within 30 km of here; otherwise none. */
/** Home: the place Ahillah is at, not one being previewed. */
const home = (): Pick<Place, 'lat' | 'lon'> => previewFrom.peek()?.home ?? place.peek();

export function keptCorrection(t = Date.now(), pl: Pick<Place, 'lat' | 'lon'> = home()): number {
  const k = loadJSON<Kept | null>('calibration', null);
  if (!k || !num(k.value, -180, 180) || !num(k.at, 0, t) || t - k.at > KEEP_HOURS * 3600e3) return 0;
  if (!num(k.lat, -90, 90) || !num(k.lon, -180, 180) || distanceKm({ lat: k.lat, lon: k.lon }, pl) > KEEP_KM) return 0;
  return k.value;
}

export function keepCorrection(value: number, t = Date.now(), pl: Pick<Place, 'lat' | 'lon'> = home()) {
  saveJSON('calibration', { value, at: t, lat: pl.lat, lon: pl.lon });
}

/** The lining up in progress: what to line up with, which of them now, and a big turn waiting to be confirmed. */
export interface Aligning { targets: AlignTarget[]; i: number; check?: number }
export const aligning = signal<Aligning | null>(null);

const set = (a: Aligning | null) => {
  aligning.value = a;
  alignReticle.value = !!a;
};

/** Shown when the sky on show is not here and now. */
export const NOT_HERE_AND_NOW = 'Align works with the sky here and now. Go back to now, at your own place, first.';

/** Start lining up (the Align chip, the offer, or Settings). False when it cannot start now. */
export function startAligning(s: AppServices): boolean {
  if (!hereAndNow()) {
    showToast(NOT_HERE_AND_NOW, 5000);
    return false;
  }
  const targets = alignTargets(s);
  if (!targets.length) {
    showToast(s.engine.context.sunAlt > -4 ? 'Nothing to align with is up just now. Try again after dark.' : 'Nothing bright enough to align with is up just now.', 5000);
    return false;
  }
  // The card goes over the sky: a card for something chosen would cover it.
  selection.value = null;
  set({ targets, i: 0 });
  return true;
}

/** Line up with the next one instead. */
export function nextTarget() {
  const a = aligning.peek();
  if (a) set({ targets: a.targets, i: (a.i + 1) % a.targets.length });
}

/** Not that one after all: back to lining up with it. */
export function tryAgain() {
  const a = aligning.peek();
  if (a) set({ targets: a.targets, i: a.i });
}

export function stopAligning() {
  set(null);
}

/**
 * The person says the circle covers the target. A turn bigger than a compass is usually out asks first (`sure` once
 * confirmed). Applied, the correction is kept for the night, with a moment to undo it.
 */
export function linedUp(s: AppServices, sure = false): 'done' | 'check' | 'no-reading' {
  const a = aligning.peek();
  if (!a) return 'done';
  if (!hereAndNow()) {
    set(null);
    showToast(NOT_HERE_AND_NOW, 5000);
    return 'done';
  }
  const o = s.engine.orientation;
  const reading = o.latestForward();
  if (!reading) {
    showToast('Hold the phone up to the sky, then tap Lined up again.', 4500);
    return 'no-reading';
  }
  // Pointing nearly straight up, the phone gives no bearing worth having: a small slip would be a big turn.
  if (enuToAltAz(reading).alt > READING_HIGHEST) {
    showToast(`Hold the phone a little lower, so the circle covers ${a.targets[a.i].label}.`, 4500);
    return 'no-reading';
  }
  // Where the target is now (the sky turns while the person looks for it). One that has since sunk too low, or may no
  // longer be used, is never lined up with where it was: another takes its place.
  const now = alignTargets(s);
  const target = now.find((x) => x.id === a.targets[a.i].id);
  if (!target) {
    if (!now.length) {
      set(null);
      showToast('Nothing bright enough to align with is up just now.', 5000);
      return 'done';
    }
    set({ targets: now, i: 0 });
    showToast(`${cap(a.targets[a.i].label)} is too low now. Line up with ${now[0].label} instead.`, 5000);
    return 'check';
  }
  const seen = refractEnu(target.enu);
  const turn = wrap180(enuToAltAz(seen).az - enuToAltAz(reading).az);
  if (!sure && Math.abs(turn) > BIG_TURN) {
    set({ ...a, check: turn });
    return 'check';
  }
  // Undo puts back what was kept before, as it was (its own time and place), and only while nothing newer has replaced
  // this alignment.
  const before = loadJSON<unknown>('calibration', null);
  o.alignTo(seen);
  keepCorrection(o.calibration);
  const mine = JSON.stringify(loadJSON<unknown>('calibration', null));
  set(null);
  const by = Math.round(Math.abs(turn));
  const undo = {
    label: 'Undo',
    run: () => {
      if (JSON.stringify(loadJSON<unknown>('calibration', null)) !== mine) return;
      saveJSON('calibration', before);
      o.calibration = keptCorrection();
    },
  };
  if (by < 2) showToast('The sky already lines up.');
  else showToast(`Aligned. The compass was ${by}° out.`, 6000, undo);
  return 'done';
}

/**
 * Keep the correction in force only while it is kept: back from the background, after a move, and every few minutes,
 * the engine's correction is what is kept for now and here (none once the night or the place has changed). Returns
 * the clean-up.
 */
export function watchAlignment(s: AppServices): () => void {
  const check = () => {
    const kept = keptCorrection();
    if (s.engine.orientation.calibration !== kept) s.engine.orientation.calibration = kept;
  };
  const onVisible = () => document.visibilityState === 'visible' && check();
  document.addEventListener('visibilitychange', onVisible);
  const unsub = place.subscribe(check);
  const id = setInterval(check, 5 * 60e3);
  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    unsub();
    clearInterval(id);
  };
}

/** The offer is on screen (the darkness check's offer waits for it). */
export const alignOfferShown = signal(false);

/** The once-a-night offer: after dark, with the sky here and now, not within 12 hours of the last, not when aligned. */
export function alignOfferDue(s: AppServices, t = Date.now()): boolean {
  const last = loadJSON<{ at?: unknown }>('alignOffer', {}).at;
  if (num(last, 0, t) && t - last < KEEP_HOURS * 3600e3) return false;
  if (keptCorrection(t)) return false;
  return hereAndNow() && s.engine.context.sunAlt < -6;
}

export function markAlignOffered(t = Date.now()) {
  saveJSON('alignOffer', { at: t });
}
